// GAVION lead statuses (spec §4): the badge shows the configured status (useLeadStatusLabel / useLeadStatusColor); beside
// it, in small grey text, the last call outcome and its age, from companies.last_call_outcome / last_called_at. This
// module formats that text. The former display codes (« À appeler », « Pas de réponse », …) are no longer the badge;
// their labels (gavion.lead_status.*) stay as the outcome labels.
import type { CallOutcome } from "../leads/types";

const OUTCOME_LABEL_KEY: Record<CallOutcome, string> = {
  no_answer: "gavion.lead_status.no_answer",
  voicemail: "gavion.lead_status.voicemail",
  talked_callback: "gavion.lead_status.callback",
  brochure_sent: "gavion.lead_status.brochure_sent",
  demo_booked: "gavion.lead_status.demo_booked",
  not_interested: "gavion.lead_status.not_interested",
  wrong_number: "gavion.call_outcome.wrong_number",
  do_not_call: "gavion.call_outcome.do_not_call",
  unlogged: "gavion.call_outcome.unlogged",
  skipped: "gavion.call_outcome.skipped",
};

/** The i18n key of an outcome's label (badge text and the « Dernier appel » filter). */
export const lastOutcomeLabelKey = (outcome: CallOutcome): string =>
  OUTCOME_LABEL_KEY[outcome];

export type OutcomeAge = { key: string; n: number };

/** « il y a 20 min » under an hour, « il y a 3 h » under a day, then « il y a 2 j »; null without a valid date. */
export const outcomeAge = (
  calledAt: string | null | undefined,
  now: Date,
): OutcomeAge | null => {
  if (!calledAt) return null;
  const t = Date.parse(calledAt);
  if (!Number.isFinite(t)) return null;
  const minutes = Math.max(0, Math.floor((now.getTime() - t) / 60_000));
  if (minutes < 60)
    return { key: "gavion.lead_status.age.minutes", n: minutes };
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return { key: "gavion.lead_status.age.hours", n: hours };
  return { key: "gavion.lead_status.age.days", n: Math.floor(hours / 24) };
};

/** « Message laissé · il y a 2 j »; null when the lead was never called. */
export const lastOutcomeText = (
  outcome: CallOutcome | null | undefined,
  calledAt: string | null | undefined,
  now: Date,
  translate: (key: string, options?: Record<string, unknown>) => string,
): string | null => {
  if (!outcome) return null;
  const label = translate(lastOutcomeLabelKey(outcome));
  const age = outcomeAge(calledAt, now);
  return age ? `${label} · ${translate(age.key, { n: age.n })}` : label;
};
